import React from "react";
import {
  Table as MuiTable,
  TableRow,
  TableHead,
  TableBody,
  TableCell as MuiTableCell,
  Typography,
  TableSortLabel,
  TablePagination,
} from "@material-ui/core";
import clsx from "clsx";
import PropTypes from "prop-types";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { noop } from "lodash";
import { useTestIds } from "../../hooks/useTestIds";

import { PermissionedDisplay } from ".";

const useStyles = makeStyles((theme) => ({
  table: {
    background: theme.palette.background.paper,
  },
  tableCell: {
    borderColor: theme.palette.primary.divider,
  },
  pinnedRow: {
    borderLeft: `8px solid ${theme.palette.primary.red}`,
  },
  tableRow: { height: "48px" },
  tableHead: {
    "& *": {
      background: theme.palette.background.paper,
    },
  },
  tableHeadCell: {
    borderColor: theme.palette.primary.divider,
    fontWeight: "bold",
    padding: theme.spacing(2),
  },
  inactive: {
    textDecoration: "line-through",
    color: "rgb(255, 255, 255, 0.5)",
  },
  textPrimary: {
    color: theme.palette.text.primary,
  },
  lastRow: {
    border: "none",
  },
  groupedRow: {
    "& > td": {
      borderBottom: "none",
      paddingBottom: 0,
    },
  },
}));

const ColumnHeader = ({ column, sortDir, sortBy, onSelectColumn }) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const testIds = useTestIds("table");

  const Content = () => {
    if (column.customHeader) {
      return column.customHeader;
    }
    return <Typography className={classes.textPrimary}>{column.name}</Typography>;
  };

  const renderColumnHeaderContent = () =>
    column.sortable ? (
      <TableSortLabel
        active={sortBy === column.canonicalName}
        direction={sortDir}
        data-testid={`${testIds.sortButton}-${column.canonicalName}`}
        onClick={() => onSelectColumn(column.canonicalName)}
      >
        <Content />
      </TableSortLabel>
    ) : (
      <Content />
    );

  return (
    <MuiTableCell
      data-column={`${column.canonicalName}-header`}
      className={classes.tableHeadCell}
      style={{ ...column.tableCellProps?.style }}
      {...column.tableHeaderProps}
    >
      {column.permissions ? (
        <PermissionedDisplay permission={column.permissions}>
          {renderColumnHeaderContent()}
        </PermissionedDisplay>
      ) : (
        renderColumnHeaderContent()
      )}
    </MuiTableCell>
  );
};
export const SortDirType = PropTypes.oneOf(["asc", "desc"]);

export const ColumnType = PropTypes.shape({
  canonicalName: PropTypes.string.isRequired,
  name: PropTypes.string,
  customHeader: PropTypes.object,
  renderCellContent: PropTypes.func,
  permissions: PropTypes.arrayOf(PropTypes.string),
  tableCellProps: PropTypes.object,
  sortable: PropTypes.bool,
});

ColumnHeader.propTypes = {
  column: ColumnType.isRequired,
  sortDir: SortDirType,
  sortBy: PropTypes.string,
  onSelectColumn: PropTypes.func,
};

ColumnHeader.defaultProps = {
  sortDir: "",
  sortBy: "",
  onSelectColumn: noop,
};

export const RowType = PropTypes.shape({
  isTableRowInactive: PropTypes.bool,
});

const TableCell = ({ row, rowIndex, column, lastRow }) => {
  const theme = useTheme();
  const classes = useStyles(theme);

  const renderCellContent = () =>
    column.renderCellContent ? column.renderCellContent(row, rowIndex) : row[column.canonicalName];

  const classNames = [classes.tableCell];
  if (row.isTableRowInactive) classNames.push(classes.inactive);
  if (lastRow) classNames.push(classes.lastRow);

  return (
    <MuiTableCell
      data-column={column.canonicalName}
      className={clsx(...classNames)}
      {...column.tableCellProps}
      style={{
        ...(row?.active === false ? { textDecoratioddn: "line-through", opacity: "0.5" } : {}),
        ...column.tableCellProps?.style,
      }}
    >
      {column.permissions ? (
        <PermissionedDisplay permission={column.permissions}>
          {renderCellContent()}
        </PermissionedDisplay>
      ) : (
        renderCellContent()
      )}
    </MuiTableCell>
  );
};

TableCell.propTypes = {
  row: RowType.isRequired,
  rowIndex: PropTypes.number.isRequired,
  column: ColumnType.isRequired,
  lastRow: PropTypes.bool,
};

TableCell.defaultProps = {
  lastRow: false,
};

const Table = ({
  rows,
  columns,
  sortProps: { sortDir, sortBy, onSelectColumn },
  paginationProps,
  muiTableProps,
  shouldGroupRow,
  pinnedRows,
  hidePagination,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const testIds = useTestIds("table");
  return (
    <MuiTable className={classes.table} data-testid={testIds.wrapper} {...muiTableProps}>
      <TableHead data-testid={testIds.head}>
        <TableRow className={classes.tableHead}>
          {columns.map((col) => (
            <ColumnHeader
              key={col.canonicalName}
              column={col}
              sortDir={sortDir}
              sortBy={sortBy}
              onSelectColumn={onSelectColumn}
            />
          ))}
        </TableRow>
      </TableHead>
      <TableBody data-testid={testIds.body}>
        {rows.map((row, idx) => (
          <TableRow
            className={`${row.id in pinnedRows ? classes.pinnedRow : classes.tableRow} ${clsx(
              shouldGroupRow(row, idx) && classes.groupedRow,
            )}`}
            key={row.id}
          >
            {columns.map((col) => (
              <TableCell
                row={row}
                rowIndex={idx}
                column={col}
                key={col.canonicalName}
                lastRow={idx === rows.length - 1 && hidePagination}
              />
            ))}
          </TableRow>
        ))}
        {!hidePagination && (
          <TableRow>
            <TablePagination
              data-testid={testIds.paginationSection}
              rowsPerPageOptions={paginationProps.rowsPerPageOptions}
              count={paginationProps.totalRowsCount}
              rowsPerPage={paginationProps.rowsPerPage}
              page={paginationProps.pageNumber}
              SelectProps={{
                "data-testid": testIds.rowsPerPage,
              }}
              backIconButtonProps={{
                "aria-label": "Previous Page",
                "data-testid": testIds.prevButton,
              }}
              nextIconButtonProps={{
                "arial-label": "Next Page",
                "data-testid": testIds.nextButton,
              }}
              onChangePage={(_, page) => paginationProps.setPageNumber(page)}
              onChangeRowsPerPage={(e) => paginationProps.setRowsPerPage(e.target.value)}
              className={classes.tableCell}
              style={{ border: "none" }}
            />
          </TableRow>
        )}
      </TableBody>
    </MuiTable>
  );
};

const SortProps = PropTypes.shape({
  sortDir: SortDirType.isRequired,
  sortBy: PropTypes.string.isRequired,
  onSelectColumn: PropTypes.func.isRequired,
});

const PaginationProps = PropTypes.shape({
  rowsPerPage: PropTypes.number,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  pageNumber: PropTypes.number,
  setPageNumber: PropTypes.func,
  setRowsPerPage: PropTypes.func,
  totalRowsCount: PropTypes.number,
});

Table.propTypes = {
  muiTableProps: PropTypes.object,
  shouldGroupRow: PropTypes.func,
  columns: PropTypes.arrayOf(ColumnType).isRequired,
  rows: PropTypes.arrayOf(RowType).isRequired,
  sortProps: SortProps,
  paginationProps: PaginationProps,
  pinnedRows: PropTypes.object,
  hidePagination: PropTypes.bool,
};

Table.defaultProps = {
  muiTableProps: {},
  shouldGroupRow: () => {},
  sortProps: {
    sortDir: "asc",
    sortBy: "",
    onSelectColumn: noop,
  },
  paginationProps: {},
  pinnedRows: {},
  hidePagination: false,
};

export default Table;
